// src/pages/VendasBalcao.jsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  collection, addDoc, updateDoc, doc, getDocs, query, orderBy, where, Timestamp, getDoc
} from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import axios from "axios";
import { useReactToPrint } from "react-to-print"; 

import { auth } from "../firebaseConfig";
import { useEmpresaId } from "../context/EmpresaContext";
import { ComprovanteVenda } from "../components/ComprovanteVenda"; 
import ModalNovaVendaBalcao from "../components/ModalNovaVendaBalcao";

// Ícones
import {
  PlusCircle, Trash2, FileText, RefreshCw, Download, Search,
  XCircle, ShoppingCart, Calendar, BadgeDollarSign, Eye,
  LayoutGrid, Table as TableIcon, Filter, Printer
} from "lucide-react";

const db = getFirestore();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";

/* ------------------------ Helpers ------------------------ */
const ensureFocusAbsolute = (maybePath) =>
  maybePath && !/^https?:\/\//i.test(maybePath)
    ? `https://api.focusnfe.com.br${maybePath}`
    : maybePath || "";

const isNfeAutorizada = (status) => {
  const s = String(status || "").toLowerCase();
  return ["autorizado", "autorizada", "emitida", "aprovada"].includes(s);
};

// header de auth
async function getAuthHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error("Usuário não autenticado");
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

function getPeriodDates(mode, selectedDate, selectedMonth, selectedYear) {
  const now = new Date();
  if (mode === "dia") {
    const d = selectedDate ? new Date(`${selectedDate}T00:00:00`) : new Date(now.toISOString().slice(0, 10));
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
    return { start, end, label: start.toLocaleDateString("pt-BR") };
  }
  if (mode === "mes") {
    const y = selectedYear || now.getFullYear();
    const m = (selectedMonth ?? now.getMonth() + 1) - 1;
    const start = new Date(y, m, 1, 0, 0, 0, 0);
    const end = new Date(y, m + 1, 1, 0, 0, 0, 0);
    return { start, end, label: `${String(m + 1).padStart(2, "0")}/${y}` };
  }
  const y = selectedYear || now.getFullYear();
  const start = new Date(y, 0, 1, 0, 0, 0, 0);
  const end = new Date(y + 1, 0, 1, 0, 0, 0, 0);
  return { start, end, label: `${y}` };
}

const NOVA_VENDA_FORM_DEFAULT = {
  clienteId: "", clienteNome: "", clienteNomeAvulso: "", produtos: [], valorTotal: 0,
};

/* ------------------------ UI helpers ------------------------ */
const toneBg = {
  success: "bg-green-100 dark:bg-green-900/50", warning: "bg-yellow-100 dark:bg-yellow-900/50",
  info: "bg-blue-100 dark:bg-blue-900/50", danger: "bg-red-100 dark:bg-red-900/40",
  neutral: "bg-gray-100 dark:bg-gray-800",
};
const toneText = {
  success: "text-green-800 dark:text-green-300", warning: "text-yellow-800 dark:text-yellow-300",
  info: "text-blue-800 dark:text-blue-300", danger: "text-red-800 dark:text-red-300",
  neutral: "text-gray-800 dark:text-gray-200",
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 text-gray-600 dark:text-gray-300">
    {Icon && <Icon size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />}
    <div>
      <span className="font-semibold text-gray-700 dark:text-gray-200">{label}</span>
      <span className="ml-1 break-words">{value}</span>
    </div>
  </div>
);

const ActionBtn = ({ tone = "neutral", icon: Icon, as = "button", className = "", disabled, children, ...props }) => {
  const base =
    tone === "primary" ? "text-blue-700 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-900"
    : tone === "danger" ? "text-red-700 bg-red-100 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-300 dark:hover:bg-red-900"
    : tone === "success" ? "text-green-700 bg-green-100 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-300 dark:hover:bg-green-900"
    : "text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700";
  const cls = `flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-md ${base} ${disabled ? "opacity-60 cursor-not-allowed" : ""} ${className}`;

  if (as === "a") {
    return <a className={cls} {...props}>{Icon && <Icon size={14} />} {children}</a>;
  }
  return <button className={cls} disabled={disabled} {...props}>{Icon && <Icon size={14} />} {children}</button>;
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white/90 dark:bg-gray-900/70 backdrop-blur p-4 shadow-sm hover:shadow-md transition">
    <div className="flex items-center gap-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#004AAD]/10 to-transparent dark:from-blue-500/20">
        <Icon className="text-[#004AAD] dark:text-blue-400 text-lg" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-xl font-bold text-gray-900 dark:text-white truncate">{value}</div>
      </div>
    </div>
  </div>
);

/* ------------------------ Card ------------------------ */
const VendaCard = ({ venda, onEmitir, onConsultar, onCancelar, onExcluir, onDetalhes, onImprimir, loadingStatus }) => {
  const autorizada = isNfeAutorizada(venda.nfeStatus);
  const cancelada = /cancelad/i.test(String(venda.nfeStatus || ""));
  const chaveClean = (venda.nfeChave || "").replace(/^NFe/, "");
  const danfeHref = venda.nfeDanfeUrl ? ensureFocusAbsolute(venda.nfeDanfeUrl) : chaveClean ? `${BACKEND_URL}/api/focus/baixar-danfe/${chaveClean}` : venda.nfeRef ? `${BACKEND_URL}/api/focus/visualizar-nfe/${encodeURIComponent(venda.nfeRef)}` : "";
  const status = String(venda.nfeStatus || "").toLowerCase();
  let pill = { text: "NFe: Não emitida", tone: "info" };
  if (autorizada) pill = { text: "NFe: autorizada", tone: "success" };
  else if (cancelada) pill = { text: "NFe: cancelada", tone: "danger" };
  else if (/rejei|deneg|erro/.test(status)) pill = { text: "NFe: rejeitada", tone: "danger" };
  else if (/process|pend|env/.test(status)) pill = { text: "NFe: em processamento", tone: "warning" };
  const dataStr = venda.dataVenda?.toDate?.().toLocaleString("pt-BR") || "Data inválida";

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md flex flex-col transition hover:shadow-xl hover:-translate-y-0.5">
      <div className="p-4 flex justify-between items-start">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-md">
            <ShoppingCart className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">{venda.clienteNome}</h3>
              {!venda.isClienteCadastrado && (
                <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300 px-2 py-0.5 rounded-full font-medium">Avulso</span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{dataStr}</p>
          </div>
        </div>
        <ActionBtn onClick={onDetalhes} icon={Eye} title="Ver detalhes">Detalhes</ActionBtn>
      </div>

      <div className="p-4 space-y-3 text-sm flex-grow border-t border-b dark:border-gray-700">
        <InfoRow icon={BadgeDollarSign} label="Valor Total:" value={Number(venda.valorTotal || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} />
        <div className="pt-2">
          <div className={`w-full text-center px-3 py-2 rounded-md text-xs font-bold ${toneBg[pill.tone]} ${toneText[pill.tone]}`}>{pill.text}</div>
        </div>
      </div>

      <div className="p-3 flex flex-wrap items-center justify-end gap-2 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
        <ActionBtn onClick={onImprimir} icon={Printer} title="Imprimir Comprovante">Imprimir</ActionBtn>
        {!autorizada && !cancelada && (
          <ActionBtn tone="danger" icon={Trash2} onClick={onExcluir} title="Excluir Venda">Excluir</ActionBtn>
        )}
        {!!danfeHref && (
          <ActionBtn as="a" href={danfeHref} target="_blank" rel="noopener noreferrer" icon={Download} title="Abrir DANFe">DANFe</ActionBtn>
        )}
        <ActionBtn tone="primary" onClick={onConsultar} icon={loadingStatus ? RefreshCw : Search} disabled={!venda.nfeRef || loadingStatus} title="Consultar status da NFe">
          {loadingStatus ? "..." : "Consultar"}
        </ActionBtn>
        {autorizada && !cancelada && (
          <ActionBtn tone="danger" onClick={onCancelar} icon={XCircle} title="Cancelar NFe">Cancelar</ActionBtn>
        )}
        {!autorizada && !cancelada && (
          <ActionBtn tone="success" onClick={onEmitir} icon={FileText} title="Emitir NFe">Emitir</ActionBtn>
        )}
      </div>
    </div>
  );
};

/* ------------------------ Modal Detalhes ------------------------ */
const VendaDetalheModal = ({ venda, onClose, onEmitir, onConsultar, onCancelar, onImprimir }) => {
  if (!venda) return null;
  const autorizada = isNfeAutorizada(venda.nfeStatus);
  const cancelada = /cancelad/i.test(String(venda.nfeStatus || ""));
  const fmt = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const chaveClean = (venda.nfeChave || "").replace(/^NFe/, "");
  const danfeHref = venda.nfeDanfeUrl ? ensureFocusAbsolute(venda.nfeDanfeUrl) : chaveClean ? `${BACKEND_URL}/api/focus/baixar-danfe/${chaveClean}` : venda.nfeRef ? `${BACKEND_URL}/api/focus/visualizar-nfe/${encodeURIComponent(venda.nfeRef)}` : "";
  const dataStr = venda.dataVenda?.toDate?.().toLocaleString("pt-BR") || "Data inválida";
  const itens = Array.isArray(venda.produtos) ? venda.produtos : [];
  const linhas = itens.map((p, idx) => {
    const qtd = Number(p.quantidade ?? p.qtd ?? 0) || 0;
    const unit = Number(p.precoUnitario ?? p.precoVenda ?? p.valorUnitario ?? p.valor ?? 0);
    const nome = p.nome ?? p.descricao ?? `Item ${idx + 1}`;
    const subtotal = qtd * unit;
    return { idx, nome, qtd, unit, subtotal };
  });
  const total = linhas.reduce((acc, l) => acc + l.subtotal, 0);
  const status = String(venda.nfeStatus || "").toLowerCase();
  let pill = { text: "NFe: Não emitida", toneBg: "bg-blue-100 dark:bg-blue-900/50", toneText: "text-blue-800 dark:text-blue-300" };
  if (autorizada) pill = { text: "NFe: autorizada", toneBg: "bg-green-100 dark:bg-green-900/50", toneText: "text-green-800 dark:text-green-300" };
  else if (cancelada) pill = { text: "NFe: cancelada", toneBg: "bg-red-100 dark:bg-red-900/50", toneText: "text-red-800 dark:text-red-300" };
  else if (/rejei|deneg|erro/.test(status)) pill = { text: "NFe: rejeitada", toneBg: "bg-red-100 dark:bg-red-900/50", toneText: "text-red-800 dark:text-red-300" };
  else if (/process|pend|env/.test(status)) pill = { text: "NFe: em processamento", toneBg: "bg-yellow-100 dark:bg-yellow-900/50", toneText: "text-yellow-800 dark:text-yellow-300" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white dark:bg-gray-900 rounded-xl shadow-xl p-6 mx-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Detalhes da venda {venda.id ? `#${String(venda.id).slice(-5)}` : ""}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{dataStr}</p>
          </div>
          <button onClick={onClose} className="px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200">Fechar</button>
        </div>

        <div className="mt-4 space-y-4">
          <div className={`inline-block px-3 py-1 rounded-md text-xs font-bold ${pill.toneBg} ${pill.toneText}`}>{pill.text}</div>

          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-500 dark:text-gray-400">Cliente</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">{venda.clienteNome || "Consumidor Final"} {!venda.isClienteCadastrado && (<span className="ml-2 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300 px-2 py-0.5 rounded-full">Avulso</span>)}</div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Valor Total</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">{fmt(venda.valorTotal)}</div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden max-h-60 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>
                  <th className="px-4 py-2 text-left">Produto</th>
                  <th className="px-4 py-2 text-center w-24">Qtd</th>
                  <th className="px-4 py-2 text-right w-32">Unitário</th>
                  <th className="px-4 py-2 text-right w-36">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {linhas.length === 0 ? (<tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">Sem itens na venda.</td></tr>) : (linhas.map((l) => (<tr key={l.idx} className="border-t dark:border-gray-700"><td className="px-4 py-2">{l.nome}</td><td className="px-4 py-2 text-center">{l.qtd}</td><td className="px-4 py-2 text-right">{fmt(l.unit)}</td><td className="px-4 py-2 text-right font-semibold">{fmt(l.subtotal)}</td></tr>)))}
              </tbody>
              <tfoot>
                <tr className="border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <td className="px-4 py-3 text-right font-semibold" colSpan={3}>Total</td>
                  <td className="px-4 py-3 text-right font-bold">{fmt(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {venda.nfeChave && <>Chave: <span className="font-mono">{venda.nfeChave}</span></>}
              {venda.nfeRef && !venda.nfeChave && <>Ref: <span className="font-mono">{venda.nfeRef}</span></>}
            </div>
            
            <div className="flex flex-wrap gap-2 justify-end w-full sm:w-auto">
              {!!danfeHref && (
                <a href={danfeHref} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-sm font-semibold">Abrir DANFe</a>
              )}
              {!autorizada && !cancelada && (
                <button onClick={onEmitir} className="px-3 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-semibold">Emitir NFe</button>
              )}
              <button onClick={onConsultar} className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">Consultar NFe</button>
              
              <button onClick={onImprimir} className="px-3 py-2 rounded-md bg-gray-600 hover:bg-gray-700 text-white text-sm font-semibold flex items-center gap-2">
                 <Printer size={16} /> Imprimir
              </button>

              {autorizada && !cancelada && (
                <button onClick={onCancelar} className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">Cancelar NFe</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------ Página ------------------------ */
const VendasBalcao = () => {
  const empresaId = useEmpresaId();
  const [vendas, setVendas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nfeLoading, setNfeLoading] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [novaVendaForm, setNovaVendaForm] = useState(NOVA_VENDA_FORM_DEFAULT);
  const [vendaDetalhe, setVendaDetalhe] = useState(null);

  // --- CONFIGURAÇÃO DE IMPRESSÃO (MODO CLÁSSICO E ROBUSTO) ---
  const printRef = useRef();
  const [dadosImpressao, setDadosImpressao] = useState(null);
  
  const handlePrintTrigger = useReactToPrint({
    content: () => printRef.current, // FUNCIONA COM A VERSÃO 2.14.15
    documentTitle: "Comprovante_Venda",
  });

const handleImprimirComprovante = (venda) => {
    // PREPARAÇÃO DOS DADOS
    const dadosFormatados = {
      ...venda,
      tipo: 'venda',
      // Garante que o total seja numérico
      total: Number(venda.valorTotal || 0),
      subtotal: Number(venda.valorTotal || 0),
      desconto: 0, 
      
      // AQUI ESTÁ A CORREÇÃO DO VALOR ZERO:
      itens: venda.produtos?.map(p => ({
        nome: p.nome,
        quantidade: Number(p.quantidade || p.qtd || 1),
        // Procura o valor em todos os campos possíveis que podem ter sido salvos
        preco: Number(
            p.precoVenda || 
            p.preco || 
            p.valorUnitario || 
            p.valor || 
            p.precoBruto || 
            0
        )
      })) || []
    };

    setDadosImpressao(dadosFormatados);
    
    // Delay para garantir que o React atualizou os dados antes de imprimir
    setTimeout(() => {
        handlePrintTrigger();
    }, 500); 
  };
  // ----------------------------------

  // Filtros
  const today = new Date();
  const [viewMode, setViewMode] = useState("cards");
  const [filterMode, setFilterMode] = useState("mes");
  const [selectedDate, setSelectedDate] = useState(() => today.toISOString().slice(0, 10));
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const periodo = useMemo(() => getPeriodDates(filterMode, selectedDate, selectedMonth, selectedYear), [filterMode, selectedDate, selectedMonth, selectedYear]);

  // Fetchers
  const buscarProdutos = useCallback(async () => {
    if (!empresaId) return setProdutos([]);
    const qy = query(collection(db, "empresas", empresaId, "produtos"), orderBy("nome"));
    const snap = await getDocs(qy);
    setProdutos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, [empresaId]);

  const buscarClientes = useCallback(async () => {
    if (!empresaId) return setClientes([]);
    const qy = query(collection(db, "empresas", empresaId, "clientes"), orderBy("nome"));
    const snap = await getDocs(qy);
    setClientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, [empresaId]);

  const buscarVendas = useCallback(async () => {
    if (!empresaId) { setVendas([]); return; }
    setLoading(true);
    try {
      const qy = query(
        collection(db, "empresas", empresaId, "vendasBalcao"),
        where("status", "==", "ativo"),
        where("dataVenda", ">=", Timestamp.fromDate(periodo.start)),
        where("dataVenda", "<", Timestamp.fromDate(periodo.end)),
        orderBy("dataVenda", "desc")
      );
      const snap = await getDocs(qy);
      setVendas(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      try {
        const qy2 = query(collection(db, "empresas", empresaId, "vendasBalcao"), where("status", "==", "ativo"), orderBy("dataVenda", "desc"));
        const snap2 = await getDocs(qy2);
        const all = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
        const ini = periodo.start.getTime();
        const fim = periodo.end.getTime();
        setVendas(all.filter((v) => (v.dataVenda?.toDate?.()?.getTime?.() ?? 0) >= ini && (v.dataVenda?.toDate?.()?.getTime?.() ?? 0) < fim));
      } catch { setVendas([]); }
    } finally { setLoading(false); }
  }, [empresaId, periodo.start, periodo.end]);

  useEffect(() => {
    if (!empresaId) { setVendas([]); setProdutos([]); setClientes([]); return; }
    buscarVendas(); buscarProdutos(); buscarClientes();
  }, [empresaId, buscarVendas, buscarProdutos, buscarClientes]);
  useEffect(() => { if (empresaId) buscarVendas(); }, [empresaId, filterMode, selectedDate, selectedMonth, selectedYear, buscarVendas]);
  // ... seus outros estados (vendas, produtos, etc) ...
  const [dadosEmpresa, setDadosEmpresa] = useState(null); // Estado para guardar os dados da loja

  // Efeito para buscar os dados da empresa no Firebase
  useEffect(() => {
    async function carregarEmpresa() {
      if (!empresaId) return;
      try {
        const docRef = doc(db, "empresas", empresaId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setDadosEmpresa(docSnap.data());
        }
      } catch (error) {
        console.error("Erro ao buscar dados da empresa:", error);
      }
    }
    carregarEmpresa();
  }, [empresaId]);

  /* ---------- Salvar venda ---------- */
  async function handleSalvarVenda(data) {
    try {
      const isClienteCadastrado = !!data.clienteId;
      const nomeFinal = isClienteCadastrado ? data.clienteNome : (data.clienteNomeAvulso?.trim() || "Consumidor Final");
      const venda = {
        clienteId: data.clienteId || null,
        clienteNome: nomeFinal,
        isClienteCadastrado,
        produtos: data.produtos || [],
        valorTotal: data.valorTotal || 0,
        dataVenda: Timestamp.now(),
        nfeStatus: "",
        nfeRef: "",
        status: "ativo",
      };
      const vendaDocRef = await addDoc(collection(db, "empresas", empresaId, "vendasBalcao"), venda);
      const nomesDosProdutos = venda.produtos.map((p) => `${p.quantidade}x ${p.nome}`).join(", ");
      await addDoc(collection(db, "empresas", empresaId, "caixa"), {
        tipo: "entrada", categoria: "Venda Balcão", descricao: `Venda Balcão #${vendaDocRef.id.slice(-5)}: ${nomesDosProdutos}`,
        valor: venda.valorTotal, data: venda.dataVenda, origemId: vendaDocRef.id, status: "ativo",
      });

      setModalOpen(false);
      setNovaVendaForm(NOVA_VENDA_FORM_DEFAULT);
      buscarVendas();
      
      const result = await Swal.fire({
        title: "Venda registrada!", text: "Deseja imprimir o comprovante?", icon: "success",
        showCancelButton: true, confirmButtonText: "Sim, Imprimir", cancelButtonText: "Não",
      });
      if (result.isConfirmed) {
        handleImprimirComprovante({ ...venda, id: vendaDocRef.id, data: venda.dataVenda.toDate() });
      }
    } catch (err) {
      console.error("Erro ao registrar venda: ", err);
      Swal.fire("Erro", "Não foi possível registrar a venda.", "error");
    }
  }

  /* ---------- NF-e ---------- */
  async function handleEmitirNfe(venda) {
    if (!empresaId) return;
    if (!venda?.clienteId) return Swal.fire("Cliente obrigatório", "Selecione um cliente cadastrado.", "warning");
    setNfeLoading((m) => ({ ...m, [venda.id]: true }));
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/focus/emissao-nfe-vb/${empresaId}`, { vendaId: venda.id }, { headers });
      await buscarVendas(); Swal.fire("Enviada!", "NF-e enviada.", "success");
    } catch (e) { Swal.fire("Erro ao emitir", e?.response?.data?.mensagem || "Falha.", "error"); }
    finally { setNfeLoading((m) => ({ ...m, [venda.id]: false })); }
  }

  async function handleConsultarNfe(venda) {
    if (!empresaId || !venda?.nfeRef) return Swal.fire("Sem referência", "Esta venda ainda não possui nfeRef.", "info");
    setNfeLoading((m) => ({ ...m, [venda.id]: true }));
    try {
      const headers = await getAuthHeaders();
      const r = await axios.get(`${BACKEND_URL}/api/focus/consultar-nfe-vb/${empresaId}/${venda.id}?empresaId=${empresaId}`, { headers, validateStatus: () => true });
      if (r.status >= 200 && r.status < 300) { await buscarVendas(); Swal.fire("Atualizado", "Status sincronizado.", "success"); }
      else if (r.status === 422) { await buscarVendas(); Swal.fire("Em processamento", r.data?.mensagem, "warning"); }
      else throw new Error(r.data?.mensagem);
    } catch (e) { Swal.fire("Erro na consulta", e?.message, "error"); }
    finally { setNfeLoading((m) => ({ ...m, [venda.id]: false })); }
  }

  async function handleCancelarNfe(venda) {
    if (!empresaId || !venda?.nfeRef) return Swal.fire("Sem NF-e", "Sem NF-e para cancelar.", "info");
    const { value: justificativa } = await Swal.fire({
      title: "Cancelar NF-e", input: "textarea", inputLabel: "Justificativa (15-255 chars)", showCancelButton: true, confirmButtonText: "Cancelar NF-e", confirmButtonColor: "#d33",
      preConfirm: (txt) => (String(txt).trim().length < 15 || String(txt).trim().length > 255) ? Swal.showValidationMessage("Min 15, Max 255 caracteres.") : txt
    });
    if (!justificativa) return;
    setNfeLoading((m) => ({ ...m, [venda.id]: true }));
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/focus/cancelar-nfe-vb/${empresaId}`, { vendaId: venda.id, justificativa }, { headers });
      await buscarVendas(); Swal.fire("Cancelada", "NF-e cancelada.", "success");
    } catch (e) { Swal.fire("Erro ao cancelar", e?.message, "error"); }
    finally { setNfeLoading((m) => ({ ...m, [venda.id]: false })); }
  }

  async function handleExcluirVenda(vendaParaExcluir) {
    const r = await Swal.fire({ title: "Excluir?", text: "Irreversível.", icon: "warning", showCancelButton: true, confirmButtonColor: "#d33" });
    if (!r.isConfirmed) return;
    try {
      await updateDoc(doc(db, "empresas", empresaId, "vendasBalcao", vendaParaExcluir.id), { status: "excluido" });
      await buscarVendas(); Swal.fire("Excluída!", "Venda removida.", "success");
    } catch { Swal.fire("Erro", "Falha ao excluir.", "error"); }
  }

  /* ---------- Render ---------- */
  const totalVendas = vendas.length;
  const valorTotalPeriodo = vendas.reduce((acc, v) => acc + (Number(v.valorTotal) || 0), 0);
  const ticketMedio = totalVendas ? valorTotalPeriodo / totalVendas : 0;
  const nfeAutorizadas = vendas.filter((v) => isNfeAutorizada(v.nfeStatus)).length;
  const fmt = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <main className="flex-grow w-full mx-auto p-6 min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header e Filtros (mantidos iguais) */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Vendas do Balcão</h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden">
            <button className={`px-3 py-2 text-sm font-semibold flex items-center gap-2 ${viewMode === "cards" ? "bg-blue-600 text-white" : "bg-white dark:bg-gray-800 text-gray-700"}`} onClick={() => setViewMode("cards")}><LayoutGrid size={16} /> Cards</button>
            <button className={`px-3 py-2 text-sm font-semibold flex items-center gap-2 ${viewMode === "table" ? "bg-blue-600 text-white" : "bg-white dark:bg-gray-800 text-gray-700"}`} onClick={() => setViewMode("table")}><TableIcon size={16} /> Tabela</button>
          </div>
          <button className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-semibold" onClick={() => { setNovaVendaForm(NOVA_VENDA_FORM_DEFAULT); setModalOpen(true); }}><PlusCircle size={18} /> Nova Venda</button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 p-4 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200"><Filter size={16} /><span className="font-semibold">Período:</span></div>
          <div className="inline-flex rounded-md overflow-hidden border dark:border-gray-700">
            {["dia", "mes", "ano"].map((m) => (<button key={m} onClick={() => setFilterMode(m)} className={`px-3 py-1.5 text-sm ${filterMode === m ? "bg-blue-600 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-700"}`}>{m === "dia" ? "Dia" : m === "mes" ? "Mês" : "Ano"}</button>))}
          </div>
          {filterMode === "dia" && (<input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="px-3 py-1.5 rounded-md border dark:border-gray-700 bg-white dark:bg-gray-900 text-sm" />)}
          {filterMode === "mes" && (<><select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="px-3 py-1.5 rounded-md border dark:border-gray-700 bg-white dark:bg-gray-900 text-sm">{Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (<option key={m} value={m}>{String(m).padStart(2, "0")}</option>))}</select><select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="px-3 py-1.5 rounded-md border dark:border-gray-700 bg-white dark:bg-gray-900 text-sm">{Array.from({ length: 8 }, (_, i) => today.getFullYear() + 1 - i).map((y) => (<option key={y} value={y}>{y}</option>))}</select></>)}
          {filterMode === "ano" && (<select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="px-3 py-1.5 rounded-md border dark:border-gray-700 bg-white dark:bg-gray-900 text-sm">{Array.from({ length: 8 }, (_, i) => today.getFullYear() + 1 - i).map((y) => (<option key={y} value={y}>{y}</option>))}</select>)}
          <button onClick={() => buscarVendas()} className="ml-auto sm:ml-0 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"><Calendar size={16} /> Atualizar</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={ShoppingCart} label="Vendas" value={totalVendas} />
        <StatCard icon={BadgeDollarSign} label="Valor total" value={fmt(valorTotalPeriodo)} />
        <StatCard icon={BadgeDollarSign} label="Ticket médio" value={fmt(ticketMedio)} />
        <StatCard icon={FileText} label="NF-e autorizadas" value={nfeAutorizadas} />
      </div>

      {viewMode === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (<p className="col-span-full text-center text-gray-500">Carregando...</p>) : vendas.length === 0 ? (<p className="col-span-full text-center text-gray-500 py-10">Nenhuma venda encontrada.</p>) : (
            vendas.map((venda) => (
              <VendaCard
                key={venda.id} venda={venda} loadingStatus={!!nfeLoading[venda.id]}
                onEmitir={() => handleEmitirNfe(venda)} onConsultar={() => handleConsultarNfe(venda)}
                onCancelar={() => handleCancelarNfe(venda)} onExcluir={() => handleExcluirVenda(venda)}
                onDetalhes={() => setVendaDetalhe(venda)} onImprimir={() => handleImprimirComprovante(venda)}
              />
            ))
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              <tr><th className="px-4 py-3 text-left">Data</th><th className="px-4 py-3 text-left">Cliente</th><th className="px-4 py-3 text-center">Itens</th><th className="px-4 py-3 text-right">Valor Total</th><th className="px-4 py-3 text-left">Status NF-e</th><th className="px-4 py-3 text-center">Ações</th></tr>
            </thead>
            <tbody>
              {loading ? (<tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Carregando...</td></tr>) : vendas.length === 0 ? (<tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Nenhuma venda encontrada.</td></tr>) : (
                vendas.map((venda) => {
                  const autorizada = isNfeAutorizada(venda.nfeStatus);
                  const cancelada = /cancelad/i.test(String(venda.nfeStatus || ""));
                  const status = String(venda.nfeStatus || "").toLowerCase();
                  let statusTxt = autorizada ? "Autorizada" : cancelada ? "Cancelada" : /rejei|deneg|erro/.test(status) ? "Rejeitada" : /process|pend|env/.test(status) ? "Processando" : "Não emitida";
                  const countItens = Array.isArray(venda.produtos) ? venda.produtos.length : 0;
                  return (
                    <tr key={venda.id} className="border-t dark:border-gray-700">
                      <td className="px-4 py-3">{venda.dataVenda?.toDate?.().toLocaleString("pt-BR") || "-"}</td><td className="px-4 py-3">{venda.clienteNome}</td><td className="px-4 py-3 text-center">{countItens}</td><td className="px-4 py-3 text-right">{fmt(venda.valorTotal)}</td><td className="px-4 py-3">{statusTxt}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2 justify-center">
                          <ActionBtn onClick={() => setVendaDetalhe(venda)} icon={Eye}>Detalhes</ActionBtn>
                          <ActionBtn onClick={() => handleImprimirComprovante(venda)} icon={Printer} title="Imprimir">Imprimir</ActionBtn>
                          <ActionBtn onClick={() => handleConsultarNfe(venda)} icon={Search} tone="primary" disabled={!venda.nfeRef}>Consultar</ActionBtn>
                          {!autorizada && !cancelada && (<ActionBtn onClick={() => handleEmitirNfe(venda)} icon={FileText} tone="success">Emitir</ActionBtn>)}
                          {autorizada && !cancelada && (<ActionBtn onClick={() => handleCancelarNfe(venda)} icon={XCircle} tone="danger">Cancelar</ActionBtn>)}
                          {!autorizada && !cancelada && (<ActionBtn onClick={() => handleExcluirVenda(venda)} icon={Trash2} tone="danger">Excluir</ActionBtn>)}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <ModalNovaVendaBalcao
        open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSalvarVenda}
        form={novaVendaForm} setForm={setNovaVendaForm} produtosDisponiveis={produtos} clientesDisponiveis={clientes}
      />

      {vendaDetalhe && (
        <VendaDetalheModal
          venda={vendaDetalhe} onClose={() => setVendaDetalhe(null)}
          onEmitir={() => handleEmitirNfe(vendaDetalhe)} onConsultar={() => handleConsultarNfe(vendaDetalhe)}
          onCancelar={() => handleCancelarNfe(vendaDetalhe)} onImprimir={() => handleImprimirComprovante(vendaDetalhe)}
        />
      )}

<div style={{ position: "absolute", top: "-9999px", left: "-9999px", visibility: "hidden" }}>
        <ComprovanteVenda 
            ref={printRef} 
            dados={dadosImpressao} 
            loja={{
                // Tenta pegar o Nome Fantasia, se não tiver, pega a Razão Social
                nome: dadosEmpresa?.nomeFantasia || dadosEmpresa?.razaoSocial || "Minha Loja",
                
                // Monta o endereço juntando rua, número e bairro (ajuste conforme seus campos no banco)
                endereco: dadosEmpresa ? `${dadosEmpresa.logradouro || 'Endereço'}, ${dadosEmpresa.numero || 'S/N'} - ${dadosEmpresa.bairro || ''}` : "Endereço não cadastrado",
                
                // Pega telefone ou celular
                telefone: dadosEmpresa?.telefone || dadosEmpresa?.celular || ""
            }}
        />
      </div>
    </main>
  );
};

export default VendasBalcao;